import { useEffect, useRef, useState } from 'react';
import { Button } from '../atoms';
import { OwnerContractFilters, OwnerContractListTable } from '../organism';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { OrderDirectionOption, OwnerContractFilterInput, PageSizeOption, useGetOwnerContractsForListQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const textsData = {
  en: {
    title: 'Owner Contracts',
    buttonText: '+ Add Owner Contract',
  },
  es: {
    title: 'Contratos de Propietarios',
    buttonText: '+ Agregar Contrato',
  }
}

const initialOwnerContractFilters: OwnerContractFilterInput = {
  status: null,
  leaseType: null,
  orderDirection: OrderDirectionOption.Desc,
  search: null,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
  startMonth: null,
  endMonth: null,
};

export const OwnerContractsListPage = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const navigate = useNavigate();
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('ownerContracts')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<OwnerContractFilterInput>(() => {
    const savedState = getListState('ownerContracts')
    return (savedState?.filters as OwnerContractFilterInput) ?? initialOwnerContractFilters
  });

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('ownerContracts')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as OwnerContractFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('ownerContracts', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: OwnerContractFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const normalizedFilters: OwnerContractFilterInput = {
    ...filters,
    page: currentPage.toString(),
    startMonth: filters.startMonth != null ? String(filters.startMonth) : null,
    endMonth: filters.endMonth != null ? String(filters.endMonth) : null,
  };

  const { data } = useGetOwnerContractsForListQuery({
    variables: {
      filterInput: normalizedFilters,
    },
  });

  const currentDataCount = data?.ownerContracts?.items?.length || 0;
  const totalPages = data?.ownerContracts?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <div className="w-full flex flex-col gap-6 p-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold bg-linear-to-r from-primary1 to-primary2 bg-clip-text text-transparent">
          {texts.title}
        </h2>
        <div>
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.CONTRACT}/${pathURL.OWNER}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </div>
      </div>
      <div>
        <OwnerContractFilters filters={filters} onFilterChange={handleFilterChange} />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <OwnerContractListTable filters={{ ...normalizedFilters }} />
      </div>
      {showPagination && (
        <div className="mt-4">
          <Pagination
            currentPage={currentPage}
            onPageChange={handlePageChange}
            currentDataCount={currentDataCount}
            pageSize={filters.pageSize || null}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
